import { gql, useMutation, useQuery, useSubscription } from '@apollo/client'
import { useMemo } from 'react'

import { useUserContext } from '@/contexts/UserContext.tsx'
import { useVersionChecker } from '@/hooks/useVersionChecker.tsx'
import { NOTIFICATION_GQL_SCHEMA } from '@/requests/apollo/schema.ts'
import { pushNotification, showBadge } from '@/utils/notification.ts'

const NOTIFICATION_COUNT_DISPLAY_LIMIT = 99

export const useNotifications = () => {
  const { user } = useUserContext()
  const systemNotification = useVersionChecker()
  const {
    data: { notifications } = { notifications: [] },
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(gql`query GetNotifications {
    notifications {
      ${NOTIFICATION_GQL_SCHEMA}
    }
  }`)

  const { loading: subscriptionLoading, error: subscriptionError } = useSubscription(
    gql`subscription NotificationSubscription {
      listenToNotifications {
        id
      }
    }`,
    {
      skip: !user,
      shouldResubscribe: true,
      onData: ({ data }) => {
        if (data) {
          refetch().then((notifications) => {
            try {
              const notificationId = data.data.listenToNotifications.id
              const notification = notifications.data.notifications.find(
                (notification: ProjectNotification) => notification?.id === notificationId,
              )
              pushNotification(notification?.message)
            } catch (e) {}
          })
        }
      },
    },
  )

  const [execute] = useMutation(
    gql`mutation ReadNotification($notificationId: ID!) {
      markNotificationAsRead(notificationId: $notificationId) {
        id
      }
    }`,
    {
      onCompleted: () => refetch(),
    },
  )

  const { allNotifications, notificationCount } = useMemo(() => {
    const allNotifications = systemNotification
      ? [systemNotification, ...notifications]
      : (notifications as Notification[])
    const unreadNotificationCount = allNotifications.filter((notification) => !notification.readAt).length

    showBadge(unreadNotificationCount)

    switch (true) {
      case unreadNotificationCount === 0:
        return {
          allNotifications,
          notificationCount: 0,
        }
      case unreadNotificationCount > NOTIFICATION_COUNT_DISPLAY_LIMIT:
        return {
          allNotifications,
          notificationCount: `${NOTIFICATION_COUNT_DISPLAY_LIMIT}+`,
        }
      default:
        return {
          allNotifications,
          notificationCount: unreadNotificationCount,
        }
    }
  }, [systemNotification, notifications])

  return {
    read: (id: string) => execute({ variables: { notificationId: id } }),
    notifications: allNotifications,
    notificationCount,
    loading: queryLoading || subscriptionLoading,
    error: queryError || subscriptionError,
  }
}
